import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BOOKING_HUB_URL } from '../utils/bookingLinks';
import { attributionBookingPayload, attributionEventParams, captureAttributionFromSearch, getAttribution, setAttributionCoupon } from '../utils/attribution';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PaxCertifiedBadge from '../components/PaxCertifiedBadge';
import PassengerConductAcknowledgment from '../components/PassengerConductAcknowledgment';
import './BookingPage.css';

const DEFAULT_AIRPORT_TAX_PER_PASSENGER_EUR = 10;

const passengerCountValue = (value) => {
  const count = Number.parseInt(value, 10);
  return Number.isFinite(count) && count > 0 ? count : 1;
};

const airportTaxPerPassengerValue = (price) => {
  const airportTax = Number(price?.airport_tax_per_passenger);
  return Number.isFinite(airportTax) && airportTax >= 0
    ? airportTax
    : DEFAULT_AIRPORT_TAX_PER_PASSENGER_EUR;
};

const bookingTotalWithAirportFee = (price, passengerCount) => {
  const baseAmount = Number(price?.amount) || 0;
  return baseAmount + passengerCountValue(passengerCount) * airportTaxPerPassengerValue(price);
};

const BookingPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, instructor } = useAuth();
  const dateLocale = i18n.language === 'hu' ? 'hu-HU' : i18n.language === 'he' ? 'he-IL' : 'en-GB';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchString = searchParams.toString();
  const codeParam = searchParams.get('code');
  const flowSlot = searchParams.get('flow') === 'slot';
  const segmentParam = searchParams.get('segment');
  const pricingIdRaw = searchParams.get('pricingId');
  const segmentLocksType = segmentParam === 'sightseeing' || segmentParam === 'intercity';

  const [selectedFlightType, setSelectedFlightType] = useState(() =>
    segmentParam === 'intercity' ? 'city_to_city' : 'city'
  );
  const [availableSlots, setAvailableSlots] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nextAvailableDate, setNextAvailableDate] = useState(null);
  const formRef = useRef(null);
  const errorRef = useRef(null);
  const beginCheckoutTrackedKeyRef = useRef('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: '',
    pilot_private_comment: '',
    payment_method: '',
    payment_amount: 0,
    voucher_code: '',
    attribution_coupon: '',
    declaration_accepted: false,
    pax_declaration_accepted: false,
    passenger_code_accepted: false,
    passenger_count: 1
  });

  const requiredSlots = selectedPricing
    ? Math.max(1, Math.ceil((selectedPricing.duration_minutes || 60) / 60))
    : 1;
  const selectedAirportTaxPerPassenger = selectedPricing
    ? airportTaxPerPassengerValue(selectedPricing)
    : DEFAULT_AIRPORT_TAX_PER_PASSENGER_EUR;
  const airportFeeTotal = passengerCountValue(formData.passenger_count) * selectedAirportTaxPerPassenger;

  useEffect(() => {
    captureAttributionFromSearch(searchString);
    const coupon = codeParam || getAttribution()?.coupon || '';
    if (coupon) {
      setFormData((prev) => ({
        ...prev,
        attribution_coupon: String(coupon).trim().toUpperCase()
      }));
    }
  }, [codeParam, searchString]);

  useEffect(() => {
    if (!showForm || selectedSlots.length === 0 || !selectedPricing) return;
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

    const slotIds = selectedSlots.map((slot) => slot.id).join(',');
    const trackingKey = `${selectedPricing.id || 'custom'}:${slotIds}:${formData.payment_amount}`;
    if (beginCheckoutTrackedKeyRef.current === trackingKey) return;
    beginCheckoutTrackedKeyRef.current = trackingKey;

    window.gtag('event', 'begin_checkout', {
      currency: 'EUR',
      value: Number(formData.payment_amount) || 0,
      ...attributionEventParams()
    });
  }, [showForm, selectedSlots, selectedPricing, formData.payment_amount]);

  useEffect(() => {
    loadAvailableSlots();
    loadPricing();
  }, [selectedDate, selectedFlightType, selectedPricing?.destination, selectedPricing?.aircraft_id, pricingIdRaw]);

  useEffect(() => {
    if (pricingIdRaw) return;
    if (segmentParam === 'intercity') setSelectedFlightType('city_to_city');
    else if (segmentParam === 'sightseeing') setSelectedFlightType('city');
  }, [segmentParam, pricingIdRaw]);

  useEffect(() => {
    if (!selectedPricing) return;
    const nextAmount = bookingTotalWithAirportFee(selectedPricing, formData.passenger_count);
    if (Number(formData.payment_amount) === nextAmount) return;
    setFormData((prev) => ({ ...prev, payment_amount: nextAmount }));
  }, [selectedPricing, formData.passenger_count, formData.payment_amount]);

  useEffect(() => {
    const loadNextAvailableDate = async () => {
      if (loading || (pricing.length > 0 && availableSlots.length > 0)) return;
      try {
        const params = { flight_type: selectedFlightType };
        if (selectedFlightType === 'city_to_city' && selectedPricing?.destination) {
          params.destination = selectedPricing.destination;
        }
        if (selectedPricing?.aircraft_id) {
          params.aircraft_id = selectedPricing.aircraft_id;
        }
        const { data } = await axios.get('/api/booking/available-slots', { params });
        const slots = data.availableSlots || [];
        const first = slots[0];
        setNextAvailableDate(first ? first.date : null);
      } catch {
        setNextAvailableDate(null);
      }
    };
    loadNextAvailableDate();
  }, [loading, pricing.length, availableSlots.length, selectedFlightType, selectedPricing?.destination, selectedPricing?.aircraft_id]);

  const loadAvailableSlots = async () => {
    setLoading(true);
    try {
      const params = { flight_type: selectedFlightType };
      if (selectedDate) params.date = selectedDate;
      if (selectedFlightType === 'city_to_city' && selectedPricing?.destination) {
        params.destination = selectedPricing.destination;
      }
      if (selectedPricing?.aircraft_id) {
        params.aircraft_id = selectedPricing.aircraft_id;
      }
      const response = await axios.get('/api/booking/available-slots', { params });
      setAvailableSlots(response.data.availableSlots);
    } catch (err) {
      setError(t('booking.errLoadSlots'));
    } finally {
      setLoading(false);
    }
  };

  const loadPricing = async () => {
    try {
      const pricingParams = { bookable: 1 };
      if (!pricingIdRaw) pricingParams.flight_type = selectedFlightType;
      const response = await axios.get('/api/pricing/active', { params: pricingParams });
      const prices = response.data.pricing || [];
      setPricing(prices);
      const urlPid = pricingIdRaw ? Number.parseInt(pricingIdRaw, 10) : NaN;
      const urlRequested = Boolean(pricingIdRaw) && !Number.isNaN(urlPid);
      const fromUrl = urlRequested ? prices.find((p) => p.id === urlPid) : null;

      if (prices.length === 0) {
        setSelectedPricing(null);
      } else if (fromUrl) {
        setSelectedPricing(fromUrl);
        setFormData((prev) => ({ ...prev, payment_amount: bookingTotalWithAirportFee(fromUrl, prev.passenger_count) }));
        setSelectedFlightType(fromUrl.flight_type === 'city_to_city' ? 'city_to_city' : 'city');
      } else if (urlRequested && !fromUrl) {
        setSelectedPricing(null);
      } else if (selectedPricing && !prices.find((p) => p.id === selectedPricing.id)) {
        const first = prices[0];
        setSelectedPricing(first);
        setFormData((prev) => ({ ...prev, payment_amount: bookingTotalWithAirportFee(first, prev.passenger_count) }));
      } else if (prices.length > 0 && !selectedPricing) {
        const first = prices[0];
        setSelectedPricing(first);
        setFormData((prev) => ({ ...prev, payment_amount: bookingTotalWithAirportFee(first, prev.passenger_count) }));
      }
    } catch (err) {
      console.error('Árak betöltési hiba:', err);
    }
  };

  const areSlotsAdjacent = (slot1, slot2) => {
    // Ellenőrzi, hogy két időpont egymás mellett van-e (ugyanaz a dátum és oktató, és az órák egymás mellett vannak)
    return slot1.date === slot2.date &&
           slot1.instructor_id === slot2.instructor_id &&
           (slot1.aircraft_id || null) === (slot2.aircraft_id || null) &&
           Math.abs(slot1.hour - slot2.hour) === 1;
  };

  const handleSlotSelect = (slot) => {
    setError('');
    setSuccess('');
    
    // Ellenőrizzük, hogy már kiválasztva van-e
    const isSelected = selectedSlots.some(s => s.id === slot.id);
    
    let newSlots;
    
    if (isSelected) {
      // Eltávolítjuk a kiválasztásból
      newSlots = selectedSlots.filter(s => s.id !== slot.id);
      setSelectedSlots(newSlots);
      if (newSlots.length === 0) {
        setShowForm(false);
      }
    } else {
      // Hozzáadjuk a kiválasztáshoz
      if (selectedSlots.length === 0) {
        // Első kiválasztás - csak hozzáadjuk
        newSlots = [slot];
        setSelectedSlots(newSlots);
        setShowForm(true);
        // Scrollozás a form-hoz
        setTimeout(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        if (selectedSlots.length >= requiredSlots) {
          setError(`Please select exactly ${requiredSlots} time slot(s) for this duration.`);
          return;
        }
        // Ellenőrizzük, hogy egymás mellett van-e a már kiválasztott időpontokkal
        const firstSlot = selectedSlots[0];
        const isAdjacent = areSlotsAdjacent(slot, firstSlot) || 
                          selectedSlots.some(s => areSlotsAdjacent(slot, s));
        
        if (!isAdjacent && selectedSlots.length > 0) {
          // Ha nem egymás mellett van, csak akkor engedélyezzük, ha ugyanaz a dátum és oktató
          const sameDateAndInstructor = slot.date === firstSlot.date && 
                                        slot.instructor_id === firstSlot.instructor_id &&
                                        (slot.aircraft_id || null) === (firstSlot.aircraft_id || null);
          
          if (!sameDateAndInstructor) {
            setError(t('booking.errSameDayPilot'));
            return;
          }
          
          // Ellenőrizzük, hogy az új időpont egymás mellett van-e valamelyik kiválasztottal
          const isAdjacentToAny = selectedSlots.some(s => areSlotsAdjacent(slot, s));
          if (!isAdjacentToAny) {
            setError(t('booking.errAdjacent'));
            return;
          }
        }
        
        // Rendezzük az időpontokat óra szerint
        newSlots = [...selectedSlots, slot].sort((a, b) => {
          if (a.date !== b.date) return a.date.localeCompare(b.date);
          return a.hour - b.hour;
        });
        setSelectedSlots(newSlots);
        setShowForm(true);
        // Scrollozás a form-hoz
        setTimeout(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
    
    if (selectedPricing && newSlots) {
      setFormData(prev => ({ ...prev, payment_amount: bookingTotalWithAirportFee(selectedPricing, prev.passenger_count) }));
    }
  };

  const handlePricingSelect = (price) => {
    setSelectedPricing(price);
    setFormData(prev => ({ ...prev, payment_amount: bookingTotalWithAirportFee(price, prev.passenger_count) }));
    setSelectedSlots([]);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (selectedSlots.length === 0) {
      setError(t('booking.errSelectOne'));
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (selectedSlots.length !== requiredSlots) {
      setError(t('booking.errSelectExact', { count: requiredSlots }));
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!formData.customer_name || formData.customer_name.trim() === '') {
      setError(t('booking.errName'));
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!formData.customer_email || formData.customer_email.trim() === '') {
      setError(t('booking.errEmail'));
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!formData.customer_phone || formData.customer_phone.trim() === '') {
      setError('Phone number is required!');
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!formData.payment_method) {
      setError(t('booking.errPayment'));
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const amount = Number(formData.payment_amount);
    if (isNaN(amount) || amount < 0) {
      setError(t('booking.errAmount'));
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (formData.payment_method === 'voucher') {
      if (!formData.voucher_code || formData.voucher_code.trim() === '') {
        setError(t('booking.errVoucherCode'));
        errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    if (!formData.declaration_accepted) {
      setError(t('costSharing.errDeclaration'));
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!formData.pax_declaration_accepted) {
      setError(t('costSharing.errPaxDeclaration'));
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!formData.passenger_code_accepted) {
      setError(t('costSharing.errPassengerConduct'));
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);

    try {
      let revolutLinkForThankYou = null;
      let passengerWeightToken = null;

      if (formData.payment_method === 'voucher') {
        const { data: validateData } = await axios.get(`/api/voucher/validate/${encodeURIComponent(formData.voucher_code.trim())}`);
        if (!validateData.valid) {
          setError(validateData.error || t('booking.errInvalidVoucher'));
          errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setSubmitting(false);
          return;
        }
        if (validateData.amount < amount) {
          setError(`Voucher value (${Number(validateData.amount).toLocaleString('en-GB')} €) is less than the booking amount (${amount.toLocaleString('en-GB')} €).`);
          errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setSubmitting(false);
          return;
        }
        const { data: voucherBookingData } = await axios.post('/api/booking/book-voucher', {
          slots: selectedSlots.map(s => ({ instructor_id: s.instructor_id, aircraft_id: s.aircraft_id || null, date: s.date, hour: s.hour })),
          customer_name: formData.customer_name.trim(),
          customer_email: formData.customer_email.trim(),
          customer_phone: formData.customer_phone.trim(),
          notes: formData.notes ? formData.notes.trim() : null,
          pilot_private_comment: formData.pilot_private_comment
            ? formData.pilot_private_comment.trim()
            : null,
          voucher_code: formData.voucher_code.trim(),
          total_amount: amount,
          declaration_accepted: true,
          pax_declaration_accepted: true,
          passenger_code_accepted: true,
          passenger_count: Number(formData.passenger_count) || 1,
          route_category: selectedFlightType === 'city_to_city' ? 'A-B' : 'A-A',
          attribution: attributionBookingPayload()
        });
        passengerWeightToken = voucherBookingData.passengerWeightToken || null;
        const bookingId = voucherBookingData?.booking?.id;
        if (typeof window !== 'undefined' && typeof window.gtag === 'function' && bookingId != null) {
          window.gtag('event', 'purchase', {
            currency: 'EUR',
            value: amount,
            transaction_id: String(bookingId),
            ...attributionEventParams()
          });
        }
      } else {
        // Revolut: find link for total amount (exact match) or use selected pricing's link
        const revolutLink = formData.payment_method === 'revolut' && selectedPricing
          ? (pricing.find(p => Number(p.amount) === amount)?.revolut_link || selectedPricing.revolut_link)
          : null;

        const bookingPromises = selectedSlots.map(slot =>
          axios.post('/api/booking/book', {
            instructor_id: slot.instructor_id,
            aircraft_id: slot.aircraft_id || null,
            date: slot.date,
            hour: slot.hour,
            customer_name: formData.customer_name.trim(),
            customer_email: formData.customer_email.trim(),
            customer_phone: formData.customer_phone.trim(),
            notes: formData.notes ? formData.notes.trim() : null,
            pilot_private_comment: formData.pilot_private_comment
              ? formData.pilot_private_comment.trim()
              : null,
            payment_method: formData.payment_method,
            payment_amount: amount,
            revolut_link: revolutLink,
            declaration_accepted: true,
            pax_declaration_accepted: true,
            passenger_code_accepted: true,
            passenger_count: Number(formData.passenger_count) || 1,
            route_category: selectedFlightType === 'city_to_city' ? 'A-B' : 'A-A',
            attribution: attributionBookingPayload()
          })
        );
        const results = await Promise.all(bookingPromises);
        revolutLinkForThankYou = results[0]?.data?.revolut_link || revolutLink;
        const bookingIds = results.map((r) => r?.data?.booking?.id).filter((id) => id != null);
        if (typeof window !== 'undefined' && typeof window.gtag === 'function' && bookingIds.length > 0) {
          window.gtag('event', 'purchase', {
            currency: 'EUR',
            value: amount,
            transaction_id: bookingIds.map(String).join('-'),
            ...attributionEventParams()
          });
        }
        if (bookingIds.length === 1) {
          passengerWeightToken = results[0]?.data?.passengerWeightToken || null;
        } else if (bookingIds.length > 1) {
          try {
            const { data: weightSessionData } = await axios.post(
              '/api/booking/passenger-weight-session-from-bookings',
              {
                booking_ids: bookingIds,
                customer_email: formData.customer_email.trim()
              }
            );
            passengerWeightToken = weightSessionData.passengerWeightToken || null;
          } catch (sessionErr) {
            console.error('passenger weight session (multi-slot):', sessionErr);
            passengerWeightToken = null;
          }
        }
      }

      // Részletes sikeres üzenet
      const firstSlot = selectedSlots[0];
      const bookingDate = new Date(firstSlot.date);
      const formattedDate = bookingDate.toLocaleDateString('hu-HU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      });
      
      const hoursList = selectedSlots.map(s => formatHour(s.hour)).join(', ');
      
      let successMessage;
      
      if (formData.payment_method === 'bank_transfer') {
        successMessage = `Thank you for your booking. We will send the payment details (bank transfer) to the email address provided shortly.`;
      } else if (formData.payment_method === 'revolut') {
        successMessage = revolutLinkForThankYou
          ? `Thank you for your booking. Pay here: ${revolutLinkForThankYou}\n\nWe have also sent the payment link to your email.`
          : `Thank you for your booking. We will send the Revolut payment details to the email address provided shortly.`;
      } else if (formData.payment_method === 'voucher') {
        successMessage = `Thank you for your booking! We look forward to seeing you at: ${formattedDate} ${hoursList}

The pilot will contact you with the flight location and details. For questions or cancellations, please email info@pandaero.com.

We have sent a confirmation email to your address.`;
      } else {
        successMessage = `Thank you for your booking! We look forward to seeing you at: ${formattedDate} ${hoursList}

The pilot will contact you with the flight location and details. For questions or cancellations, please email info@pandaero.com.

We have sent a confirmation email to your address.`;
      }

      setShowForm(false);
      setSelectedSlots([]);
      setSelectedPricing(null);
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        notes: '',
        pilot_private_comment: '',
        payment_method: '',
        payment_amount: 0,
        voucher_code: '',
        attribution_coupon: getAttribution()?.coupon || '',
        declaration_accepted: false,
        pax_declaration_accepted: false,
        passenger_code_accepted: false,
        passenger_count: 1
      });
      if (passengerWeightToken) {
        navigate(`/booking/passenger-weights/${encodeURIComponent(passengerWeightToken)}`, {
          state: { thankYouMessage: successMessage, revolut_link: revolutLinkForThankYou }
        });
      } else {
        navigate('/booking/thank-you?type=booking', { state: { message: successMessage, revolut_link: revolutLinkForThankYou } });
      }
    } catch (err) {
      const message =
        err.response?.data?.error ||
        (err.code === 'ERR_NETWORK' ? 'Network error – please check your internet connection.' : err.message || 'Booking error');
      setError(message);
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(dateLocale, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      weekday: 'long'
    });
  };

  const formatHour = (hour) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  // Csoportosítás dátum szerint
  const groupedSlots = availableSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const today = new Date().toISOString().split('T')[0];

  const pricingIdLocked =
    Boolean(pricingIdRaw) &&
    selectedPricing != null &&
    Number(selectedPricing.id) === Number.parseInt(pricingIdRaw, 10);
  const hideFlightTypeSelect = segmentLocksType || pricingIdLocked;

  return (
    <div className="container">
      <h1>{t('booking.title')}</h1>
      {flowSlot && (
        <p className="booking-focused-context">
          {t('booking.focusedSlotSubtitle')}{' '}
          <Link to={BOOKING_HUB_URL} className="booking-focused-context-link">
            {t('booking.openFullBookingMenu')}
          </Link>
        </p>
      )}

      {!flowSlot && (
        <>
          <div className="booking-voucher-option card">
            <p className="booking-voucher-option-text">{t('booking.voucherHint')}</p>
            <Link to="/booking/voucher" className="btn btn-outline-booking-page">
              {t('booking.buyVoucher')}
            </Link>
          </div>

          <div className="booking-companions-option card">
            <p className="booking-companions-option-text">{t('booking.companionsHint')}</p>
            <Link to="/booking/companions" className="btn btn-outline-booking-page">
              {t('home.findCompanions')}
            </Link>
          </div>
        </>
      )}

      <div ref={errorRef}>
        {error && <div className="error" role="alert">{error}</div>}
        {success && <div className="success">{success}</div>}
      </div>

      <div className="booking-content">
        <div className="booking-filters">
          <div className="form-group">
            <label>{t('booking.flightType')}</label>
            {hideFlightTypeSelect ? (
              <p className="booking-flight-type-locked">
                {selectedFlightType === 'city_to_city' ? t('booking.cityToCity') : t('booking.citySightseeing')}
                <span className="booking-flight-type-locked-note"> ({t('booking.flightTypeLockedHint')})</span>
              </p>
            ) : (
              <select
                value={selectedFlightType}
                onChange={(e) => {
                  setSelectedFlightType(e.target.value);
                  setSelectedSlots([]);
                  setSelectedPricing(null);
                  setShowForm(false);
                }}
              >
                <option value="city">{t('booking.citySightseeing')}</option>
                <option value="city_to_city">{t('booking.cityToCity')}</option>
              </select>
            )}
            {selectedFlightType === 'city_to_city' && (
              <small className="form-hint" style={{ display: 'block', marginTop: '4px' }}>
                {t('booking.budapestHint')}
              </small>
            )}
          </div>
          <div className="form-group">
            <label>{t('booking.filterDate')}</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={today}
            />
            {selectedDate && (
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setSelectedDate('');
                  loadAvailableSlots();
                }}
                style={{ marginTop: '10px' }}
              >
                Clear filter
              </button>
            )}
          </div>
        </div>

        {pricing.length > 0 && (
          <div className="booking-package-selection card">
            <h3>{t('booking.selectPackage')}</h3>
            <div className="pricing-options pricing-options-inline">
              {pricing.map(price => (
                <div
                  key={price.id}
                  className={`pricing-option ${selectedPricing?.id === price.id ? 'selected' : ''}`}
                  onClick={() => handlePricingSelect(price)}
                >
                  <div className="pricing-option-header">
                    <strong>{price.name}</strong>
                    <span className="pricing-option-amount">
                      {parseInt(price.amount).toLocaleString('en-GB')} €
                    </span>
                  </div>
                  <span className="pricing-option-duration">{price.duration_minutes || 60} min</span>
                  {price.destination && (
                    <span className="pricing-option-destination">{t('booking.budapestTo', { dest: price.destination })}</span>
                  )}
                  <span className="pricing-option-destination">
                    {t('booking.airportFeePerPassenger', {
                      fee: airportTaxPerPassengerValue(price).toLocaleString('en-GB')
                    })}
                  </span>
                  {price.description && (
                    <p className="pricing-option-desc">
                      {price.description
                        .replace(/Alfa HUB/gi, 'PandAero Clubtag')
                        .replace(/február\s+végéig/gi, 'Február 28.-ig')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {pricing.length === 0 && !loading && (
          <div className="card">
            <p>{t('booking.noPackages', { type: selectedFlightType === 'city' ? t('booking.citySightseeing') : t('booking.cityToCity') })}</p>
            {nextAvailableDate && (
              <p>{t('booking.nextAvailabilityOn', { date: formatDate(nextAvailableDate) })}</p>
            )}
            <p>{t('booking.noPackagesContact')}</p>
            {selectedFlightType === 'city_to_city' && (
              <p className="booking-request-cta">
                <Link to="/booking/request-flight" className="btn btn-outline-booking-page">
                  {t('booking.requestFlight')}
                </Link>
                <span className="booking-request-hint">{t('booking.requestFlightHint')}</span>
              </p>
            )}
          </div>
        )}

        {loading ? (
          <div className="loading">{t('booking.loading')}</div>
        ) : availableSlots.length === 0 && pricing.length > 0 ? (
          <div className="card">
            <p>{t('booking.noSlots')}</p>
            {nextAvailableDate && (
              <p>{t('booking.nextAvailabilityOn', { date: formatDate(nextAvailableDate) })}</p>
            )}
            <p>{t('booking.noSlotsContact')}</p>
            {selectedFlightType === 'city_to_city' && selectedPricing?.destination && (
              <p className="booking-request-cta">
                <Link
                  to={`/booking/request-flight?destination=${encodeURIComponent(selectedPricing.destination)}&date=${selectedDate || ''}`}
                  className="btn btn-outline-booking-page"
                >
                  {t('booking.requestFlight')}
                </Link>
                <span className="booking-request-hint">{t('booking.requestFlightHint')}</span>
              </p>
            )}
          </div>
        ) : pricing.length > 0 ? (
          <>
            <div className="slots-container">
              <h3>{t('booking.selectSlots', { count: requiredSlots })}</h3>
              {Object.entries(groupedSlots).map(([date, slots]) => (
                <div key={date} className="date-group">
                  <h3>{formatDate(date)}</h3>
                  <div className="slots-grid">
                    {slots.map(slot => {
                      const isSelected = selectedSlots.some(s => s.id === slot.id);
                      return (
                        <button
                          key={slot.id}
                          className={`slot-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleSlotSelect(slot)}
                        >
                          <div className="slot-time">{formatHour(slot.hour)}</div>
                          <div className="slot-instructor slot-pilot-row">
                            <span className="slot-pilot-label">{t('booking.pilot')}</span>
                            <PaxCertifiedBadge pilotName={slot.instructor_name} showTooltip={true} inline={true} />
                          </div>
                          <div className="slot-instructor">
                            <Link
                              to={`/pilot/${slot.instructor_id}`}
                              className="slot-pilot-profile-link"
                              onClick={(ev) => ev.stopPropagation()}
                            >
                              {t('booking.viewPilotProfile')}
                            </Link>
                          </div>
                          <div className="slot-instructor">
                            <small>
                              {[slot.aircraft_type, slot.aircraft_registration].filter(Boolean).join(' · ') ||
                                t('pilotProfile.pending')}
                            </small>
                          </div>
                          <div className="slot-instructor">
                            <small>
                              {t('booking.pilotExperience', {
                                total: slot.total_flight_hours ?? 0,
                                recent: slot.recent_flight_hours ?? 0
                              })}
                            </small>
                          </div>
                          {(slot.ratings || slot.licence_qualification) && (
                            <div className="slot-instructor">
                              <small>
                                {[slot.licence_qualification, slot.ratings].filter(Boolean).join(' · ')}
                              </small>
                            </div>
                          )}
                          {slot.hours_on_type != null && slot.hours_on_type !== '' && (
                            <div className="slot-instructor">
                              <small>
                                {t('booking.hoursOnTypeShort', { hours: slot.hours_on_type })}
                              </small>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {selectedSlots.length > 0 && (
              <div className="scroll-indicator" onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                <span>↓</span>
              </div>
            )}

            {showForm && selectedSlots.length > 0 && (
              <div ref={formRef} className="card booking-form-card">
                <h2>{t('booking.bookingDetails')}</h2>
                <div className="selected-slots-info">
                  <p><strong>{t('booking.selectedSlots')}</strong></p>
                  {selectedSlots.map((slot, index) => (
                    <p key={slot.id}>
                      {index + 1}. {formatDate(slot.date)} {formatHour(slot.hour)} - {t('booking.pilot')}: <PaxCertifiedBadge pilotName={slot.instructor_name} showTooltip={true} inline={true} />
                    </p>
                  ))}
                </div>
                
                <form onSubmit={handleSubmit} noValidate>
                  <div className="form-group">
                    <label>{t('booking.name')} {t('common.required')}</label>
                    <input
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('booking.couponCode')}</label>
                    <input
                      type="text"
                      value={formData.attribution_coupon}
                      onChange={(e) => {
                        const coupon = e.target.value.toUpperCase();
                        setFormData({ ...formData, attribution_coupon: coupon });
                        setAttributionCoupon(coupon);
                      }}
                      placeholder="LEGENDA10"
                    />
                    <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                      {t('booking.couponCodeHint')}
                    </small>
                  </div>
                  <div className="form-group">
                    <label>{t('booking.email')} {t('common.required')}</label>
                    <input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('booking.phone')} {t('common.required')}</label>
                    <input
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('booking.notes')}</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows="3"
                    />
                  </div>


                  <div className="form-group">
                    <label>{t('booking.pilotPrivateComment')}</label>
                    <textarea
                      value={formData.pilot_private_comment}
                      onChange={(e) =>
                        setFormData({ ...formData, pilot_private_comment: e.target.value })
                      }
                      rows="2"
                      placeholder={t('booking.pilotPrivateCommentPh')}
                    />
                    <small className="form-hint">{t('booking.pilotPrivateCommentHint')}</small>
                  </div>

                  <div className="cost-sharing-disclaimer">
                    <p className="cost-sharing-risk-text">
                      {t('costSharing.riskDisclaimer')}
                    </p>
                    <label className="cost-sharing-declaration-row">
                      <input
                        type="checkbox"
                        checked={formData.declaration_accepted}
                        onChange={(e) => setFormData({...formData, declaration_accepted: e.target.checked})}
                      />
                      <span className="declaration-text">{t('costSharing.declarationLabel')}</span>
                    </label>
                  </div>

                  <div className="pax-certificate-disclaimer">
                    <p className="pax-certificate-risk-text">
                      {t('costSharing.paxDisclaimer')}
                    </p>
                    <label className="pax-certificate-declaration-row">
                      <input
                        type="checkbox"
                        checked={formData.pax_declaration_accepted}
                        onChange={(e) => setFormData({...formData, pax_declaration_accepted: e.target.checked})}
                      />
                      <span className="declaration-text">{t('costSharing.paxDeclarationLabel')}</span>
                    </label>
                  </div>
                  <PassengerConductAcknowledgment
                    checked={formData.passenger_code_accepted}
                    onChange={(accepted) => setFormData({ ...formData, passenger_code_accepted: accepted })}
                  />
                  <div className="form-group">
                    <label>Number of passengers</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={formData.passenger_count}
                      onChange={(e) => {
                        const passenger_count = e.target.value;
                        setFormData({
                          ...formData,
                          passenger_count,
                          payment_amount: selectedPricing
                            ? bookingTotalWithAirportFee(selectedPricing, passenger_count)
                            : formData.payment_amount
                        });
                      }}
                    />
                    <small style={{color: '#666', display: 'block', marginTop: '5px'}}>
                      {t('booking.passengerAirportTaxNotice', {
                        fee: selectedAirportTaxPerPassenger.toLocaleString('en-GB')
                      })}
                    </small>
                  </div>
                  
                  <div className="payment-section">
                    <h3>{t('booking.paymentInfo')}</h3>
                    <div className="form-group">
                      <label>{t('booking.paymentMethod')} {t('common.required')}</label>
                      <select
                        value={formData.payment_method}
                        onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                        required
                      >
                        <option value="">{t('booking.selectPayment')}</option>
                        <option value="cash">{t('booking.cash')}</option>
                        <option value="bank_transfer">{t('booking.bankTransfer')}</option>
                        <option value="revolut">{t('booking.revolut')}</option>
                        <option value="voucher">{t('booking.voucher')}</option>
                      </select>
                    </div>
                    {formData.payment_method === 'voucher' && (
                      <div className="form-group">
                        <label>{t('booking.voucherCode')} {t('common.required')}</label>
                        <input
                          type="text"
                          value={formData.voucher_code}
                          onChange={(e) => setFormData({...formData, voucher_code: e.target.value.toUpperCase()})}
                          placeholder="e.g. VA-XXXXXX"
                          required={formData.payment_method === 'voucher'}
                        />
                        <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                          {t('booking.voucherCodeHint')}
                        </small>
                      </div>
                    )}
                    <div className="form-group">
                      <label>{t('booking.amountToPay')} {t('common.required')}</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.payment_amount}
                        onChange={(e) => setFormData({...formData, payment_amount: e.target.value})}
                        required
                        placeholder="e.g. 50"
                        readOnly={selectedPricing !== null}
                        style={selectedPricing ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                      />
                      {selectedPricing && <small style={{color: '#666', display: 'block', marginTop: '5px'}}>{t('booking.amountHint')}</small>}
                      {selectedPricing && (
                        <small style={{color: '#666', display: 'block', marginTop: '5px'}}>
                          {t('booking.airportFeeHint', {
                            fee: selectedAirportTaxPerPassenger.toLocaleString('en-GB'),
                            passengers: passengerCountValue(formData.passenger_count),
                            total: airportFeeTotal.toLocaleString('en-GB')
                          })}
                        </small>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={submitting}
                    >
                      {submitting ? t('booking.submitting') : t('booking.confirmBooking')}
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                    onClick={() => {
                      setShowForm(false);
                      setSelectedSlots([]);
                      setSelectedPricing(null);
                    }}
                    >
                      {t('booking.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default BookingPage;
